import { c as _c } from "react/compiler-runtime";
import type { CSSProperties } from "react";

/**
 * Tiny card sprite, matches the in-game `NTinyCard` used on the run-history /
 * game-over screen. Composites six layers:
 *
 *   1. card_back.png    , tinted by card pool/character
 *   2. desc_box.png     , dark description area
 *   3. {type}_portrait_shadow.png
 *   4. {type}_portrait.png
 *   5. banner_shadow.png
 *   6. banner.png       , tinted by card rarity
 *
 * Sprites are served from `/static/images/ui/run_history_card/`. Tint colors
 * come directly from the decompiled game code (`NTinyCard.GetBannerColor` and
 * `CardPoolModel.DeckEntryCardColor`). See /developers for the full recipe.
 */

import { imageUrl } from "@/lib/image-url";
const BASE = imageUrl("/static/images/ui/run_history_card");

// CardPoolModel.DeckEntryCardColor, one per character/pool.
export const TINY_CARD_POOL_COLOR: Record<string, string> = {
  ironclad: "#D62000",
  silent: "#5EBD00",
  defect: "#3EB3ED",
  necrobinder: "#CD4EED",
  regent: "#E36600",
  colorless: "#A3A3A3",
  event: "#A3A3A3",
  curse: "#585B61",
  quest: "#24476A",
  status: "#FFFFFF",
  token: "#FFFFFF"
};

// NTinyCard.GetBannerColor, one per rarity.
export const TINY_CARD_BANNER_COLOR: Record<string, string> = {
  Basic: "#9C9C9C",
  Starter: "#9C9C9C",
  Common: "#9C9C9C",
  Uncommon: "#64FFFF",
  Rare: "#FFDA36",
  Curse: "#E669FF",
  Event: "#13BE1A",
  Ancient: "#13BE1A",
  Quest: "#F46836",
  Status: "#9C9C9C",
  Token: "#9C9C9C"
};

// NTinyCard.SetCardPortraitShape, Attack/Power have their own, everything
// else falls through to the skill portrait.
function portraitShape(type: string | undefined): "attack" | "power" | "skill" {
  if (type === "Attack") return "attack";
  if (type === "Power") return "power";
  return "skill";
}
function maskStyle(src: string, bg: string): CSSProperties {
  return {
    backgroundColor: bg,
    WebkitMaskImage: `url(${src})`,
    maskImage: `url(${src})`,
    WebkitMaskSize: "contain",
    maskSize: "contain",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskPosition: "center",
    maskPosition: "center"
  };
}
export interface TinyCardProps {
  /** Card pool / character id, lowercase (`ironclad`, `silent`, `regent`, …). */
  color?: string;
  /** Card type: `Attack`, `Skill`, `Power`, `Status`, `Curse`, … */
  type?: string;
  /** Card rarity: `Common`, `Uncommon`, `Rare`, `Curse`, `Event`, `Quest`, … */
  rarity?: string;
  /** Tailwind size classes. Default `w-6 h-6`. */
  className?: string;
}
export default function TinyCard(t0) {
  const $ = _c(23);
  const {
    color,
    type,
    rarity,
    className: t1
  } = t0;
  const className = t1 === undefined ? "w-6 h-6" : t1;
  const back = TINY_CARD_POOL_COLOR[(color ?? "").toLowerCase()] ?? "#FFFFFF";
  const banner = TINY_CARD_BANNER_COLOR[rarity ?? ""] ?? "#FFFFFF";
  let t2;
  if ($[0] !== type) {
    t2 = portraitShape(type);
    $[0] = type;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const shape = t2;
  const t3 = `relative inline-block flex-shrink-0 ${className}`;
  let t4;
  if ($[2] !== back) {
    t4 = maskStyle(`${BASE}/card_back.png`, back);
    $[2] = back;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t4) {
    t5 = <span className="absolute inset-0" style={t4} />;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <img src={`${BASE}/desc_box.png`} alt="" className="absolute inset-0 w-full h-full object-contain opacity-25" crossOrigin="anonymous" />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = `${BASE}/${shape}_portrait_shadow.png`;
  let t8;
  if ($[7] !== t7) {
    t8 = <img src={t7} alt="" className="absolute inset-0 w-full h-full object-contain" crossOrigin="anonymous" />;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const t9 = `${BASE}/${shape}_portrait.png`;
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      filter: "brightness(0.95) sepia(0.15)"
    };
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== t9) {
    t11 = <img src={t9} alt="" className="absolute inset-0 w-full h-full object-contain" style={t10} crossOrigin="anonymous" />;
    $[10] = t9;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <img src={`${BASE}/banner_shadow.png`} alt="" className="absolute inset-0 w-full h-full object-contain opacity-60" crossOrigin="anonymous" />;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== banner) {
    t13 = maskStyle(`${BASE}/banner.png`, banner);
    $[13] = banner;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] !== t13) {
    t14 = <span className="absolute inset-0" style={t13} />;
    $[15] = t13;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== t11 || $[18] !== t14 || $[19] !== t3 || $[20] !== t5 || $[21] !== t8) {
    t15 = <span className={t3}>{t5}{t6}{t8}{t11}{t12}{t14}</span>;
    $[17] = t11;
    $[18] = t14;
    $[19] = t3;
    $[20] = t5;
    $[21] = t8;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  return t15;
}